import './CreatorFilterPanel.scss';
import { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';
import {
  CreatorAttributeFilters,
  CreatorFilterState
} from '@blisspointmedia/bpm-types/dist/YoutubeCreatorInsights';
import { AudienceInsightsDimensions } from '@blisspointmedia/bpm-types/dist/YoutubePlanning';
import { Button } from '@Components/Button';
import { Size } from '@Components/designConstants';
import { Dropdown } from '@Components/Dropdown';
import { MenuItem } from '@Components/Dropdown/dropdown-constants';
import { InfoTooltip } from '@Components/InfoTooltip';
import { SegmentedControlButton } from '@Components/SegmentedControlButton';
import { Mixpanel, MxE } from '@src/utils/mixpanelWrapper';
import { conditionallyPluralizeWithCount } from '@src/utils/text-utils';
import { PoweredByGoogle } from '../PoweredByGoogle';
import { PAGE_TITLE } from '../youtube-planning-constants';
import {
  AGE_RANGE_OPTIONS,
  AUDIENCE_SEARCH_TOOLTIP,
  AudienceOptionFetcher,
  CreatorSearchDraft,
  CreatorSearchLabels,
  DEVICE_OPTIONS,
  EMPTY_DRAFT,
  GENDER_OPTIONS,
  INCOME_RANGE_OPTIONS,
  INTEREST_DIMENSIONS,
  INTEREST_KEY_BY_DIMENSION,
  InterestIdKey,
  PARENTAL_STATUS_OPTIONS,
  SEARCH_MODE_OPTIONS,
  SearchMode,
  TOPIC_DIMENSIONS,
  TOPIC_SEARCH_TOOLTIP
} from './creator-discovery-constants';
import {
  fromCreatorFilterState,
  getInterestIds,
  isSearchable,
  parseChannelIds,
  toCreatorFilterState,
  toLabelledOptions,
  withInterestSelection
} from './creator-discovery-utils';

interface CreatorFilterPanelProps {
  // The panel copies these once; remount it (via `key`) to load a different search.
  initialSearch?: CreatorFilterState;
  initialLabels?: CreatorSearchLabels;
  fetchAudienceOptions: AudienceOptionFetcher;
  disabled?: boolean;
  searching?: boolean;
  onSearch: (search: CreatorFilterState, labels: CreatorSearchLabels) => void;
}

export const CreatorFilterPanel = ({
  initialSearch,
  initialLabels = {},
  fetchAudienceOptions,
  disabled = false,
  searching = false,
  onSearch
}: CreatorFilterPanelProps) => {
  const [draft, setDraft] = useState<CreatorSearchDraft>(() =>
    initialSearch ? fromCreatorFilterState(initialSearch) : EMPTY_DRAFT
  );
  const [labels, setLabels] = useState<CreatorSearchLabels>(initialLabels);
  // Which id list a typeahead result belongs in, from the dimension it came back with.
  const [interestKeys, setInterestKeys] = useState<Record<string, InterestIdKey>>({});

  const search = useMemo(() => toCreatorFilterState(draft), [draft]);

  const fetchOptions = useCallback(
    async (input: string, dimensions: AudienceInsightsDimensions[]) => {
      const items = await fetchAudienceOptions(input, dimensions);
      setLabels(prev => ({
        ...prev,
        ...Object.fromEntries(items.map(item => [item.value, item.label ?? item.value]))
      }));
      setInterestKeys(prev => ({
        ...prev,
        ...Object.fromEntries(
          items.flatMap(item => {
            const key = item.dimension ? INTEREST_KEY_BY_DIMENSION[item.dimension] : undefined;
            return key ? [[item.value, key]] : [];
          })
        )
      }));
      return items;
    },
    [fetchAudienceOptions]
  );

  return (
    <div className='creator-filter-panel'>
      <div className='planning-form-header'>
        <div className='header'>Discover Creators</div>
        <PoweredByGoogle />
      </div>
      <div className='planning-form-body'>
        <div className='planning-form-section'>
          <div className='planning-form-section-title'>Search By</div>
          <SegmentedControlButton
            options={SEARCH_MODE_OPTIONS}
            selectedOption={draft.mode}
            disabled={disabled}
            onChange={mode => {
              setDraft(prev => ({ ...prev, mode }));
              Mixpanel.track(MxE.TabChange, { page: PAGE_TITLE, option_selected: mode });
            }}
          />
        </div>
        <ModeInputs
          draft={draft}
          setDraft={setDraft}
          labels={labels}
          interestKeys={interestKeys}
          fetchOptions={fetchOptions}
          disabled={disabled}
        />
      </div>
      <div className='planning-form-footer'>
        <Button
          size={Size.LG}
          disabled={disabled || !isSearchable(search)}
          loading={searching}
          onClick={() => onSearch(search, labels)}
        >
          Search Creators
        </Button>
      </div>
    </div>
  );
};

interface ModeInputsProps {
  draft: CreatorSearchDraft;
  setDraft: Dispatch<SetStateAction<CreatorSearchDraft>>;
  labels: CreatorSearchLabels;
  interestKeys: Record<string, InterestIdKey>;
  fetchOptions: AudienceOptionFetcher;
  disabled: boolean;
}

const ModeInputs = ({
  draft,
  setDraft,
  labels,
  interestKeys,
  fetchOptions,
  disabled
}: ModeInputsProps) => {
  const fetchInterests = useCallback(
    (input: string) => fetchOptions(input, INTEREST_DIMENSIONS),
    [fetchOptions]
  );
  const fetchTopics = useCallback(
    (input: string) => fetchOptions(input, TOPIC_DIMENSIONS),
    [fetchOptions]
  );

  const setAttributes = (update: (prev: CreatorAttributeFilters) => CreatorAttributeFilters) =>
    setDraft(prev => ({ ...prev, attributes: update(prev.attributes) }));

  if (draft.mode === SearchMode.CHANNELS) {
    return (
      <div className='planning-form-section'>
        <div className='planning-form-section-title'>Channels</div>
        <Dropdown
          searchable
          creatable
          multiSelect
          showChips
          size={Size.LG}
          className='creator-typeahead'
          placeholder='Paste one or more YouTube channel IDs (UC…)'
          options={toLabelledOptions(draft.channelIds, labels)}
          value={draft.channelIds}
          reactSelectProps={{
            isValidNewOption: input => parseChannelIds(input).length > 0,
            formatCreateLabel: input =>
              `Add ${conditionallyPluralizeWithCount(parseChannelIds(input).length, 'channel')}`,
            onCreateOption: input =>
              setDraft(prev => ({
                ...prev,
                channelIds: [...new Set([...prev.channelIds, ...parseChannelIds(input)])]
              }))
          }}
          onChange={channelIds => setDraft(prev => ({ ...prev, channelIds }))}
          disabled={disabled}
        />
      </div>
    );
  }

  if (draft.mode === SearchMode.TOPICS) {
    return (
      <div className='planning-form-section'>
        <div className='planning-form-section-title'>
          Topics
          <InfoTooltip size={Size.SM}>{TOPIC_SEARCH_TOOLTIP}</InfoTooltip>
        </div>
        <Dropdown
          searchable
          multiSelect
          showChips
          size={Size.LG}
          className='creator-typeahead'
          placeholder='Search for a topic, brand or product...'
          options={toLabelledOptions(draft.topicEntityIds, labels)}
          value={draft.topicEntityIds}
          optionFetcher={fetchTopics}
          onChange={topicEntityIds => setDraft(prev => ({ ...prev, topicEntityIds }))}
          disabled={disabled}
        />
      </div>
    );
  }

  const interestIds = getInterestIds(draft.attributes);
  return (
    <>
      <div className='planning-form-section'>
        <div className='planning-form-section-title'>
          Audience Interests
          <InfoTooltip size={Size.SM}>{AUDIENCE_SEARCH_TOOLTIP}</InfoTooltip>
        </div>
        <Dropdown
          searchable
          multiSelect
          showChips
          size={Size.LG}
          className='creator-typeahead'
          placeholder='Search for a category, interest or brand...'
          options={toLabelledOptions(interestIds, labels)}
          value={interestIds}
          optionFetcher={fetchInterests}
          onChange={values =>
            setAttributes(prev => withInterestSelection(prev, values, value => interestKeys[value]))
          }
          disabled={disabled}
        />
      </div>
      <div className='planning-form-section'>
        <div className='planning-form-section-title'>Audience Demographics</div>
        <div className='audience-targeting-container'>
          <AttributeDropdown
            title='Age Range'
            options={AGE_RANGE_OPTIONS}
            value={draft.attributes.ageRanges ?? []}
            onChange={ageRanges => setAttributes(prev => ({ ...prev, ageRanges }))}
            disabled={disabled}
          />
          <AttributeDropdown
            title='Gender'
            options={GENDER_OPTIONS}
            value={draft.attributes.genders ?? []}
            onChange={genders => setAttributes(prev => ({ ...prev, genders }))}
            disabled={disabled}
          />
          <AttributeDropdown
            title='Parental Status'
            options={PARENTAL_STATUS_OPTIONS}
            value={draft.attributes.parentalStatuses ?? []}
            onChange={parentalStatuses => setAttributes(prev => ({ ...prev, parentalStatuses }))}
            disabled={disabled}
          />
          <AttributeDropdown
            title='Household Income'
            options={INCOME_RANGE_OPTIONS}
            value={draft.attributes.incomeRanges ?? []}
            onChange={incomeRanges => setAttributes(prev => ({ ...prev, incomeRanges }))}
            disabled={disabled}
          />
          <AttributeDropdown
            title='Device'
            options={DEVICE_OPTIONS}
            value={draft.attributes.devices ?? []}
            onChange={devices => setAttributes(prev => ({ ...prev, devices }))}
            disabled={disabled}
          />
        </div>
      </div>
    </>
  );
};

interface AttributeDropdownProps<T extends string> {
  title: string;
  options: MenuItem<T>[];
  value: T[];
  onChange: (value: T[]) => void;
  disabled: boolean;
}

const AttributeDropdown = <T extends string>({
  title,
  options,
  value,
  onChange,
  disabled
}: AttributeDropdownProps<T>) => (
  <div className='audience-targeting-dropdown-container'>
    <div className='dropdown-title'>{title}</div>
    <Dropdown<T>
      size={Size.LG}
      className='audience-targeting-dropdown'
      placeholder='Any'
      options={options}
      value={value}
      onChange={onChange}
      multiSelect
      clearable
      disabled={disabled}
    />
  </div>
);
